import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useStore } from '../../context/StoreContext';
import { sendManualInvoice, fetchAuditLogs } from '../../lib/api';
import { Button } from '../../components/ui/Button';
import { formatCurrency, cn } from '../../lib/utils';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { format, set } from 'date-fns';

const generateTimeSlots = () => {
  const slots = [];
  for (let i = 0; i < 24 * 2; i++) {
    const minutes = i * 30;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const timeString = `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
    const date = set(new Date(), { hours, minutes: mins, seconds: 0 });
    const label = format(date, 'h:mm a');
    slots.push({ value: timeString, label });
  }
  return slots;
};

const TIME_SLOTS = generateTimeSlots();

const StatusBadge = ({ status }) => {
  const styles = {
    pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
    confirmed: "bg-green-100 text-green-800 border-green-200",
    declined: "bg-red-100 text-red-800 border-red-200",
    cancelled: "bg-gray-100 text-gray-800 border-gray-200"
  };

  const icons = {
    pending: FiIcons.FiClock,
    confirmed: FiIcons.FiCheckCircle,
    declined: FiIcons.FiXCircle,
    cancelled: FiIcons.FiSlash
  };

  return (
    <span className={cn("flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border uppercase tracking-wide", styles[status])}>
      <SafeIcon icon={icons[status]} />
      {status}
    </span>
  );
};

const PaymentStatusBadge = ({ status }) => {
  const styles = {
    unpaid: "bg-red-50 text-red-700 border-red-200",
    deposit_paid: "bg-amber-50 text-amber-700 border-amber-200",
    paid_in_full: "bg-emerald-50 text-emerald-700 border-emerald-200",
  };
  const labels = {
    unpaid: "Unpaid",
    deposit_paid: "Deposit Paid",
    paid_in_full: "Paid in Full",
  };
  return (
    <span className={cn("px-2 py-0.5 rounded-full text-xs font-bold border", styles[status] || styles.unpaid)}>
      {labels[status] || status}
    </span>
  );
};

const OfflinePaymentBadge = () => (
  <span className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border bg-orange-100 text-orange-800 border-orange-200 uppercase tracking-wide">
    <SafeIcon icon={FiIcons.FiCreditCard} />
    Offline Payment
  </span>
);

const formatAuditAction = (log) => {
  const action = log.action;
  const newVal = log.new_value || {};

  switch (action) {
    case 'status_changed':
      if (newVal.status === 'confirmed') return 'Booking approved by Admin';
      if (newVal.status === 'declined') return 'Booking declined by Admin';
      if (newVal.status === 'pending') return 'Booking reopened by Admin';
      return `Status changed to ${newVal.status || 'unknown'}`;
    case 'cancelled':
      return 'Booking cancelled by Admin';
    case 'updated':
      return 'Booking details edited by Admin';
    case 'balance_manually_cleared':
      return 'Balance manually marked as paid by Admin';
    case 'manual_invoice_sent':
      return 'Balance invoice manually sent by Admin';
    case 'confirmation_attempted':
      return 'Confirmation email triggered';
    case 'automated_reminder_sent': {
      const days = newVal.daysUntilEvent;
      return days
        ? `Automated payment reminder sent (${days} days before event)`
        : 'Automated payment reminder sent';
    }
    default:
      return action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }
};

const BookingsList = () => {
  const { bookings, isLoading, updateBookingStatus, cancelBooking, manuallyClearBalance, updateBookingDetails, updateBookingPaymentLink, eventTypes, addOns } = useStore();
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState('pending');
  const [expandedId, setExpandedId] = useState(null);
  const [editingBooking, setEditingBooking] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [showEditModal, setShowEditModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [actionError, setActionError] = useState('');
  const [declineTarget, setDeclineTarget] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  const [invoiceLoading, setInvoiceLoading] = useState(null);
  const [invoiceSent, setInvoiceSent] = useState(null);
  const [manualOverride, setManualOverride] = useState(false);
  const [overridePrice, setOverridePrice] = useState('');
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [balanceClearTarget, setBalanceClearTarget] = useState(null);
  const [balanceClearLoading, setBalanceClearLoading] = useState(false);
  const [policySnapshotModal, setPolicySnapshotModal] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const autoExpandedRef = useRef(false);
  useEffect(() => {
    if (autoExpandedRef.current || isLoading) return;
    const bookingId = searchParams.get('bookingId');
    if (!bookingId) return;
    if (bookings.some(booking => booking.id === bookingId)) {
      setFilter('all');
      setExpandedId(bookingId);
    }
    autoExpandedRef.current = true;
  }, [isLoading, bookings, searchParams]);

  useEffect(() => {
    if (!expandedId) { setAuditLogs([]); return; }
    let cancelled = false;
    setAuditLoading(true);
    fetchAuditLogs(expandedId)
      .then(logs => { if (!cancelled) setAuditLogs(logs); })
      .catch(() => { if (!cancelled) setAuditLogs([]); })
      .finally(() => { if (!cancelled) setAuditLoading(false); });
    return () => { cancelled = true; };
  }, [expandedId]);

  const counts = useMemo(() => ({
    all: bookings.length,
    pending: bookings.filter(b => b.status === 'pending').length,
    confirmed: bookings.filter(b => b.status === 'confirmed').length,
    declined: bookings.filter(b => b.status === 'declined').length,
  }), [bookings]);

  const sortedBookings = useMemo(() => [...bookings].sort((a, b) => {
    if (a.status === 'pending' && b.status !== 'pending') return -1;
    if (a.status !== 'pending' && b.status === 'pending') return 1;
    return new Date(b.createdAt) - new Date(a.createdAt);
  }), [bookings]);

  const filteredBookings = filter === 'all'
    ? sortedBookings
    : sortedBookings.filter(b => b.status === filter);

  const resolveAddOnName = (addonId) => {
    const addon = addOns.find(a => a.id === addonId);
    return addon ? addon.name : addonId.slice(-8);
  };

  const handleEditClick = (booking) => {
    setEditingBooking(booking);
    setEditFormData({
      ...booking,
      eventType: booking.eventType?.id || booking.eventType
    });
    setManualOverride(false);
    setOverridePrice('');
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (editFormData.startTime >= editFormData.endTime) {
      alert('Error: End time must be after start time.');
      return;
    }

    if (manualOverride && (!overridePrice || isNaN(Number(overridePrice)) || Number(overridePrice) < 0)) {
      alert('Error: Please enter a valid price for the manual override.');
      return;
    }

    setActionLoading('save');
    setActionError('');
    try {
      const eventTypeObj = eventTypes.find(et => et.id === editFormData.eventType) || editFormData.eventType;
      const payload = {
        ...editingBooking,
        ...editFormData,
        eventType: eventTypeObj,
        eventTypeId: typeof editFormData.eventType === 'string' ? editFormData.eventType : editFormData.eventType?.id,
      };
      if (manualOverride) {
        payload.manualPriceOverride = Number(overridePrice);
      }
      await updateBookingDetails(payload);
      setShowEditModal(false);
      setEditingBooking(null);
      setManualOverride(false);
      setOverridePrice('');
    } catch (err) {
      if (err.message?.includes('VERSION_MISMATCH')) {
        alert('This booking was modified by another user. Please refresh the page and try again.');
      } else {
        setActionError(err.message || 'Failed to save changes.');
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleStatusChange = async (bookingId, status) => {
    setActionLoading(bookingId + status);
    setActionError('');
    try {
      await updateBookingStatus(bookingId, status);
    } catch (err) {
      alert(err.message || 'Failed to update status.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeclineClick = (booking) => {
    setDeclineTarget(booking);
    setDeclineReason('');
  };

  const handleConfirmDecline = async () => {
    if (!declineTarget) return;
    setActionLoading(declineTarget.id + 'declined');
    try {
      await updateBookingStatus(declineTarget.id, 'declined');
      setDeclineTarget(null);
    } catch (err) {
      alert(err.message || 'Failed to decline booking.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSendInvoice = async (bookingId, invoiceType = 'balance_payment') => {
    setInvoiceLoading(bookingId);
    try {
      const result = await sendManualInvoice(bookingId, invoiceType);
      if (result.paymentLink) {
        updateBookingPaymentLink(bookingId, result.paymentLink, invoiceType);
      }
      setInvoiceSent(bookingId);
      setTimeout(() => setInvoiceSent(null), 5000);
    } catch (err) {
      alert(err.message || 'Failed to send invoice.');
    } finally {
      setInvoiceLoading(null);
    }
  };

  const handleCancelClick = (booking) => {
    setCancelTarget(booking);
  };

  const handleConfirmCancel = async () => {
    if (!cancelTarget) return;
    setCancelLoading(true);
    try {
      await cancelBooking(cancelTarget.id);
      setCancelTarget(null);
    } catch (err) {
      alert(err.message || 'Failed to cancel booking.');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleConfirmBalanceClear = async () => {
    if (!balanceClearTarget) return;
    setBalanceClearLoading(true);
    try {
      await manuallyClearBalance(balanceClearTarget.id);
      setBalanceClearTarget(null);
    } catch (err) {
      alert(err.message || 'Failed to clear balance.');
    } finally {
      setBalanceClearLoading(false);
    }
  };

  const filterTabs = [
    { key: 'pending', label: 'Pending' },
    { key: 'confirmed', label: 'Confirmed' },
    { key: 'declined', label: 'Declined' },
    { key: 'all', label: 'All' },
  ];

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto pb-24">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Booking Requests</h1>
          <p className="text-gray-500 mt-1">Manage incoming requests and scheduled events</p>
        </div>

        <div className="flex gap-1 bg-white p-1 rounded-lg border border-gray-200 shadow-sm">
          {filterTabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={cn(
                "px-3 py-2 rounded-md text-sm font-bold transition-all flex items-center gap-1.5",
                filter === tab.key
                  ? "bg-gray-900 text-white shadow-sm"
                  : "text-gray-600 hover:bg-gray-50"
              )}
            >
              {tab.label}
              <span className={cn(
                "text-xs px-1.5 py-0.5 rounded-full font-bold min-w-[20px] text-center",
                filter === tab.key
                  ? "bg-white/20 text-white"
                  : "bg-gray-100 text-gray-500"
              )}>
                {counts[tab.key]}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-4">
        {filteredBookings.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-gray-300">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiIcons.FiInbox} className="text-3xl text-gray-400" />
            </div>
            <h3 className="text-lg font-bold text-gray-900">No bookings found</h3>
            <p className="text-gray-500">There are no booking requests in this category.</p>
          </div>
        ) : (
          filteredBookings.map(booking => {
            const isExpanded = expandedId === booking.id;
            return (
              <div key={booking.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                {/* Header row */}
                <div className="p-5 flex flex-col md:flex-row justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <StatusBadge status={booking.status} />
                      {booking.isOfflinePayment && <OfflinePaymentBadge />}
                      <span className="text-xs font-mono text-gray-400">#{booking.id.slice(-8)}</span>
                      <span className="text-xs text-gray-400 ml-auto md:ml-2">
                        {format(new Date(booking.createdAt), 'MMM d, h:mm a')}
                      </span>
                    </div>

                    <h3 className="text-lg font-bold text-gray-900 truncate">{booking.eventType?.name || 'Unknown Event Type'}</h3>

                    <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-gray-600 mt-2">
                      <div className="flex items-center gap-1.5">
                        <SafeIcon icon={FiIcons.FiCalendar} className="text-gray-400 text-xs" />
                        <span className="font-semibold">{format(new Date(booking.date + 'T00:00:00'), 'MMM do, yyyy')}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <SafeIcon icon={FiIcons.FiClock} className="text-gray-400 text-xs" />
                        <span>
                          {TIME_SLOTS.find(t => t.value === booking.startTime)?.label || booking.startTime} - {TIME_SLOTS.find(t => t.value === booking.endTime)?.label || booking.endTime}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <SafeIcon icon={FiIcons.FiUsers} className="text-gray-400 text-xs" />
                        <span>{booking.guestCount} guests</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <SafeIcon icon={FiIcons.FiDollarSign} className="text-gray-400 text-xs" />
                        <span className="font-bold">{formatCurrency(booking.totalPrice)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    {booking.status === 'pending' && (
                      <Button
                        size="sm"
                        variant="danger"
                        className="font-bold"
                        onClick={() => handleDeclineClick(booking)}
                        isLoading={actionLoading === booking.id + 'declined'}
                      >
                        <SafeIcon icon={FiIcons.FiX} className="mr-1" />
                        Decline
                      </Button>
                    )}
                    {booking.status !== 'pending' && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs"
                        onClick={() => handleStatusChange(booking.id, 'pending')}
                        isLoading={actionLoading === booking.id + 'pending'}
                      >
                        Reopen
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEditClick(booking)}
                    >
                      <SafeIcon icon={FiIcons.FiEdit2} />
                    </Button>
                    <button
                      onClick={() => setExpandedId(isExpanded ? null : booking.id)}
                      className={cn(
                        "p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-all",
                        isExpanded && "bg-gray-100"
                      )}
                      title={isExpanded ? 'Collapse' : 'View Details'}
                    >
                      <SafeIcon icon={isExpanded ? FiIcons.FiChevronUp : FiIcons.FiChevronDown} className="text-gray-500" />
                    </button>
                  </div>
                </div>

                {/* Expanded detail panel */}
                {isExpanded && (
                  <div className="border-t border-gray-100 bg-gray-50 p-5">
                    <div className="grid md:grid-cols-3 gap-6">
                      <div>
                        <h4 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">Contact Information</h4>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <SafeIcon icon={FiIcons.FiUser} className="text-gray-400 text-sm" />
                            <span className="text-sm font-semibold text-gray-900">{booking.contactName}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <SafeIcon icon={FiIcons.FiMail} className="text-gray-400 text-sm" />
                            <a href={`mailto:${booking.contactEmail}`} className="text-sm text-blue-600 hover:underline">{booking.contactEmail}</a>
                          </div>
                          <div className="flex items-center gap-2">
                            <SafeIcon icon={FiIcons.FiPhone} className="text-gray-400 text-sm" />
                            <span className="text-sm text-gray-700">{booking.contactPhone}</span>
                          </div>
                        </div>

                        <h4 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2 mt-4">Last Communication</h4>
                        {(() => {
                          const commActions = ['automated_reminder_sent', 'manual_invoice_sent', 'booking_created'];
                          const lastComm = auditLogs
                            .filter(log => log.entity_id === booking.id && commActions.includes(log.action))
                            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];
                          if (!lastComm) {
                            return <p className="text-sm text-gray-400 italic">No communications recorded</p>;
                          }
                          const actionLabels = {
                            automated_reminder_sent: 'Automated reminder sent',
                            manual_invoice_sent: 'Manual invoice sent',
                            booking_created: 'Booking confirmed',
                          };
                          return (
                            <div className="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                              <SafeIcon icon={FiIcons.FiMessageCircle} className="text-blue-600 flex-shrink-0" />
                              <span className="text-sm text-blue-900 font-medium">
                                {format(new Date(lastComm.timestamp), 'MMM d, h:mm a')} - {actionLabels[lastComm.action] || lastComm.action}
                              </span>
                            </div>
                          );
                        })()}
                      </div>

                      <div>
                        <h4 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">Financial Summary</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-500">Total Price</span>
                            <span className="font-bold text-gray-900">{formatCurrency(booking.totalPrice)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Deposit Required</span>
                            <span className="font-semibold text-gray-900">{formatCurrency(booking.depositAmount)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Amount Paid</span>
                            <span className="font-semibold text-emerald-700">{formatCurrency(booking.amountPaid)}</span>
                          </div>
                          <div className="flex justify-between items-center pt-1 border-t border-gray-200">
                            <span className="text-gray-700 font-semibold">Balance Due</span>
                            <span className={cn("font-bold", booking.balanceDue > 0 ? "text-red-700" : "text-emerald-700")}>
                              {formatCurrency(booking.balanceDue)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center pt-1">
                            <span className="text-gray-500">Payment Status</span>
                            <PaymentStatusBadge status={booking.paymentStatus} />
                          </div>
                          {booking.depositPaymentIntent && (
                            <div className="flex justify-between items-center pt-1">
                              <span className="text-gray-500">Deposit Payment ID</span>
                              <a
                                href={`https://dashboard.stripe.com/payments/${booking.depositPaymentIntent}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-mono text-xs text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                {booking.depositPaymentIntent}
                              </a>
                            </div>
                          )}
                          {booking.balancePaymentIntent && (
                            <div className="flex justify-between items-center pt-1">
                              <span className="text-gray-500">Balance Payment ID</span>
                              <a
                                href={`https://dashboard.stripe.com/payments/${booking.balancePaymentIntent}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-mono text-xs text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                {booking.balancePaymentIntent}
                              </a>
                            </div>
                          )}
                          {booking.isOfflinePayment && (
                            <div className="flex items-center gap-2 mt-3 p-2.5 bg-orange-50 border border-orange-200 rounded-lg">
                              <SafeIcon icon={FiIcons.FiAlertTriangle} className="text-orange-600 flex-shrink-0" />
                              <span className="text-xs font-bold text-orange-800">Offline Payment - No Stripe transaction exists. Collect payment directly.</span>
                            </div>
                          )}
                        </div>

                        {(booking.status === 'pending' || booking.status === 'confirmed') && (
                          <div className="mt-4">
                            {booking.balanceDue === 0 || booking.paymentStatus === 'paid_in_full' ? (
                              <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                                <SafeIcon icon={FiIcons.FiCheckCircle} className="text-emerald-600" />
                                <span className="text-sm font-bold text-emerald-700">Paid in Full</span>
                              </div>
                            ) : invoiceSent === booking.id ? (
                              <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                                <SafeIcon icon={FiIcons.FiCheck} className="text-blue-600" />
                                <span className="text-sm font-bold text-blue-700">
                                  {booking.amountPaid === 0 ? 'Deposit link sent successfully!' : 'Invoice sent successfully!'}
                                </span>
                              </div>
                            ) : booking.amountPaid === 0 && booking.depositAmount > 0 ? (
                              <Button
                                size="sm"
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold"
                                onClick={() => handleSendInvoice(booking.id, 'deposit')}
                                isLoading={invoiceLoading === booking.id}
                              >
                                <SafeIcon icon={FiIcons.FiSend} className="mr-1.5" />
                                Approve &amp; Send Deposit Link ({formatCurrency(booking.depositAmount)})
                              </Button>
                            ) : booking.balanceDue > 0 ? (
                              <Button
                                size="sm"
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold"
                                onClick={() => handleSendInvoice(booking.id, 'balance_payment')}
                                isLoading={invoiceLoading === booking.id}
                              >
                                <SafeIcon icon={FiIcons.FiSend} className="mr-1.5" />
                                Send Final Invoice ({formatCurrency(booking.balanceDue)})
                              </Button>
                            ) : null}
                          </div>
                        )}
                      </div>

                      <div>
                        <h4 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">Event Details</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-500">Event Type</span>
                            <span className="font-semibold text-gray-900">{booking.eventType?.name || 'Unknown'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Guest Count</span>
                            <span className="font-semibold text-gray-900">{booking.guestCount}</span>
                          </div>
                          {booking.accessCode && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">Access Code</span>
                              <span className="font-mono font-bold text-primary">{booking.accessCode}</span>
                            </div>
                          )}
                        </div>

                        <h4 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2 mt-4">Add-ons</h4>
                        {booking.selectedAddOns && booking.selectedAddOns.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {booking.selectedAddOns.map(addonId => {
                              const isBundled = booking.eventType?.bundledAddons?.includes(addonId);
                              return (
                                <span key={addonId} className="bg-white border border-gray-200 px-2.5 py-1 rounded-lg text-xs font-semibold text-gray-700 shadow-sm">
                                  {resolveAddOnName(addonId)}
                                  {isBundled && <span className="text-green-600 ml-1">(Bundled)</span>}
                                </span>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-400 italic">No add-ons selected</p>
                        )}
                      </div>
                    </div>

                    <div className="mt-5 pt-5 border-t border-gray-200">
                      <h4 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Description of Use</h4>
                      <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">{booking.descriptionOfUse || 'No description provided.'}</p>
                      </div>
                    </div>

                    <div className="mt-4">
                      <h4 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Additional Notes</h4>
                      {booking.notes ? (
                        <p className="text-sm text-gray-600 italic whitespace-pre-wrap">{booking.notes}</p>
                      ) : (
                        <p className="text-sm text-gray-400 italic">No notes provided.</p>
                      )}
                    </div>

                    <div className="mt-5 pt-5 border-t border-gray-200">
                      <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Policy Acceptance Log</h4>
                      {booking.policyAcceptedAt ? (
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-gray-500">
                            Terms accepted on: <span className="font-bold text-gray-700">{format(new Date(booking.policyAcceptedAt), 'MMM d, yyyy h:mm a')}</span>
                          </p>
                          <button
                            type="button"
                            onClick={() => setPolicySnapshotModal(booking)}
                            className="text-xs text-primary font-bold hover:underline"
                          >
                            View Agreed Policy
                          </button>
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400 italic">No policy snapshot recorded</p>
                      )}
                    </div>

                    {(booking.status === 'pending' || booking.status === 'confirmed') && (
                      <div className="mt-5 pt-5 border-t border-gray-200">
                        <h4 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">Admin Actions</h4>
                        <div className="flex flex-wrap gap-3">
                          {booking.status === 'confirmed' && booking.balanceDue > 0 && (
                            <Button
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
                              onClick={() => setBalanceClearTarget(booking)}
                            >
                              <SafeIcon icon={FiIcons.FiDollarSign} className="mr-1.5" />
                              Mark Balance as Paid
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="danger"
                            className="font-bold"
                            onClick={() => handleCancelClick(booking)}
                          >
                            <SafeIcon icon={FiIcons.FiXCircle} className="mr-1.5" />
                            Cancel Booking
                          </Button>
                        </div>
                      </div>
                    )}

                    <div className="mt-5 pt-5 border-t border-gray-200">
                      <h4 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">System History</h4>
                      {auditLoading ? (
                        <p className="text-xs text-gray-400 italic">Loading history...</p>
                      ) : auditLogs.length === 0 ? (
                        <p className="text-xs text-gray-400 italic">No history recorded</p>
                      ) : (
                        <div className="relative pl-5">
                          <div className="absolute left-[7px] top-1 bottom-1 w-px bg-gray-200" />
                          <div className="space-y-3">
                            {auditLogs.map((log) => (
                              <div key={log.id} className="relative flex items-start gap-3">
                                <div className="absolute left-[-13px] top-1.5 w-2.5 h-2.5 rounded-full bg-gray-300 border-2 border-white ring-1 ring-gray-200" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-gray-800 font-medium">{formatAuditAction(log)}</p>
                                  <p className="text-xs text-gray-400 mt-0.5">{format(new Date(log.timestamp), 'MMM d, yyyy h:mm a')}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Decline Reason Modal */}
      {declineTarget && (
        <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">Decline Booking</h3>
              <p className="text-sm text-gray-500 mt-1">
                Decline request from <span className="font-semibold">{declineTarget.contactName}</span> for {declineTarget.eventType?.name || 'event'}?
              </p>
            </div>
            <div className="p-6">
              <label className="block text-sm font-bold text-gray-700 mb-2">Reason (optional)</label>
              <textarea
                className="w-full p-3 border border-gray-300 rounded-lg h-24 text-sm placeholder:text-gray-400 focus:ring-2 focus:ring-red-200 focus:border-red-400 outline-none"
                placeholder="Optionally explain why this booking was declined..."
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
              />
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3 bg-gray-50 rounded-b-2xl">
              <Button variant="outline" onClick={() => setDeclineTarget(null)}>Cancel</Button>
              <Button
                variant="danger"
                onClick={handleConfirmDecline}
                isLoading={actionLoading === declineTarget.id + 'declined'}
              >
                Confirm Decline
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Booking Modal */}
      {cancelTarget && (
        <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">Cancel Booking</h3>
              <p className="text-sm text-gray-500 mt-1">
                Are you sure you want to cancel the booking from <span className="font-semibold">{cancelTarget.contactName}</span> for {cancelTarget.eventType?.name || 'event'} on {format(new Date(cancelTarget.date + 'T00:00:00'), 'MMM do, yyyy')}?
              </p>
              <p className="text-sm text-red-600 mt-3 font-medium">This will instantly release this date/time back to the public calendar. Any financial refunds must be processed manually in your Stripe Dashboard.</p>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3 bg-gray-50 rounded-b-2xl">
              <Button variant="outline" onClick={() => setCancelTarget(null)}>Keep Booking</Button>
              <Button
                variant="danger"
                onClick={handleConfirmCancel}
                isLoading={cancelLoading}
              >
                Confirm Cancellation
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Mark Balance as Paid Modal */}
      {balanceClearTarget && (
        <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">Mark Balance as Paid</h3>
              <p className="text-sm text-gray-500 mt-1">
                Clear the outstanding balance of <span className="font-bold text-gray-900">{formatCurrency(balanceClearTarget.balanceDue)}</span> for <span className="font-semibold">{balanceClearTarget.contactName}</span>?
              </p>
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800 font-medium">This will mark the account as settled and queue the booking for access code delivery. This action cannot be undone.</p>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3 bg-gray-50 rounded-b-2xl">
              <Button variant="outline" onClick={() => setBalanceClearTarget(null)}>Cancel</Button>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
                onClick={handleConfirmBalanceClear}
                isLoading={balanceClearLoading}
              >
                Confirm Payment
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingBooking && (
        <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
              <h3 className="text-xl font-bold text-gray-900">Edit Booking #{editingBooking.id.slice(-6)}</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <SafeIcon icon={FiIcons.FiX} className="text-2xl" />
              </button>
            </div>

            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Customer Name</label>
                <input
                  type="text"
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  value={editFormData.contactName}
                  onChange={(e) => setEditFormData({...editFormData, contactName: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Phone Number</label>
                <input
                  type="text"
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  value={editFormData.contactPhone}
                  onChange={(e) => setEditFormData({...editFormData, contactPhone: e.target.value})}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-1">Customer Email</label>
                <input
                  type="email"
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  value={editFormData.contactEmail}
                  onChange={(e) => setEditFormData({...editFormData, contactEmail: e.target.value})}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-1">Number of Guests</label>
                <input
                  type="number"
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  value={editFormData.guestCount}
                  onChange={(e) => setEditFormData({...editFormData, guestCount: parseInt(e.target.value)})}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-1">Event Type</label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  value={editFormData.eventType}
                  onChange={(e) => setEditFormData({...editFormData, eventType: e.target.value})}
                >
                  {eventTypes.map(et => (
                    <option key={et.id} value={et.id}>{et.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  value={editFormData.date}
                  onChange={(e) => setEditFormData({...editFormData, date: e.target.value})}
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Start Time</label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  value={editFormData.startTime}
                  onChange={(e) => setEditFormData({...editFormData, startTime: e.target.value})}
                >
                  {TIME_SLOTS.map(slot => (
                    <option key={`start-${slot.value}`} value={slot.value}>{slot.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">End Time</label>
                <select
                  className={cn(
                    "w-full p-2 border rounded-lg transition-colors",
                    editFormData.startTime >= editFormData.endTime
                      ? "border-red-500 bg-red-50 text-red-900"
                      : "border-gray-300"
                  )}
                  value={editFormData.endTime}
                  onChange={(e) => setEditFormData({...editFormData, endTime: e.target.value})}
                >
                  {TIME_SLOTS.map(slot => (
                    <option key={`end-${slot.value}`} value={slot.value}>{slot.label}</option>
                  ))}
                </select>
                {editFormData.startTime >= editFormData.endTime && (
                  <p className="text-xs text-red-600 mt-1 font-bold">End time must be after start time.</p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-1">Description of Use</label>
                <textarea
                  className="w-full p-2 border border-gray-300 rounded-lg h-24"
                  value={editFormData.descriptionOfUse}
                  onChange={(e) => setEditFormData({...editFormData, descriptionOfUse: e.target.value})}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-1">Additional Notes</label>
                <textarea
                  className="w-full p-2 border border-gray-300 rounded-lg h-24"
                  value={editFormData.notes || ''}
                  onChange={(e) => setEditFormData({...editFormData, notes: e.target.value})}
                />
              </div>

              <div className="md:col-span-2 border-t border-gray-200 pt-4 mt-2">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <label className="text-sm font-bold text-gray-700">Manual Price Override</label>
                    <p className="text-xs text-gray-500 mt-0.5">Skip automatic pricing and set a custom total</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setManualOverride(!manualOverride);
                      if (!manualOverride) setOverridePrice(String(editingBooking.totalPrice));
                    }}
                    className={cn(
                      "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                      manualOverride ? "bg-blue-600" : "bg-gray-300"
                    )}
                  >
                    <span className={cn(
                      "inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm",
                      manualOverride ? "translate-x-6" : "translate-x-1"
                    )} />
                  </button>
                </div>
                {manualOverride && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <label className="block text-sm font-bold text-blue-800 mb-1">Total Price (USD)</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">$</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="w-full pl-7 p-2 border border-blue-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none"
                        value={overridePrice}
                        onChange={(e) => setOverridePrice(e.target.value)}
                        placeholder="600.00"
                      />
                    </div>
                    <p className="text-xs text-blue-600 mt-2">
                      Balance due will be recalculated as: {formatCurrency(Math.max(0, Number(overridePrice || 0) - (editingBooking?.amountPaid || 0)))}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3 bg-gray-50">
              <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
              <Button
                onClick={handleSaveEdit}
                disabled={editFormData.startTime >= editFormData.endTime}
                className={cn(editFormData.startTime >= editFormData.endTime && "opacity-50 cursor-not-allowed")}
              >
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}

      {policySnapshotModal && (
        <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center bg-gray-50">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Agreed Policy Snapshot</h3>
                <p className="text-xs text-gray-500 mt-1">
                  Accepted by {policySnapshotModal.contactName} ({policySnapshotModal.contactEmail}) on {format(new Date(policySnapshotModal.policyAcceptedAt), 'MMMM d, yyyy h:mm a')}
                </p>
              </div>
              <button
                onClick={() => setPolicySnapshotModal(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <SafeIcon icon={FiIcons.FiX} className="text-2xl" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
                {policySnapshotModal.acceptedPolicySnapshot || 'No policy text was recorded for this booking.'}
              </p>
            </div>
            <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end">
              <Button variant="outline" onClick={() => setPolicySnapshotModal(null)}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingsList;
